import { api } from '../config/api'
import { locales } from '../constants/languages'
import store from '../store'
import { isError } from '../utils/api'
import { generateFilters } from '../utils/filters'
import LocalStorageService from './LocalStorageService'

const defaultLang = locales.spanish
const userBaseEndpoint = '/AccountApi'

export const getUserInfo = () => api.get(`${userBaseEndpoint}/Me`)

export const loginUser = async (email, password, subdomain) => {
  let response
  if (subdomain) {
    response = await api.post(`${userBaseEndpoint}/${subdomain}/SignIn`, { email, password })
  } else {
    response = await api.post(`${userBaseEndpoint}/SignIn`, { email, password })
  }
  const token = response.data.token
  LocalStorageService.setAuthToken(token)
  return response
}

export const logoutUser = () => {
  LocalStorageService.removeAuthToken()
  store.dispatch('logout')
}

export const isLoggedIn = () => !!LocalStorageService.getAuthToken()

export const searchUsers = (filters) =>
  api.get(`${userBaseEndpoint}/GetAllUsersFromSchoolGroup`, { params: { ...filters } })

export const sendRecoverPasswordMail = email => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/ResetPasswordEmail`, { email }, { params: { locale } })
}

export const changePassword = (userId, resetToken, newPassword) =>
  api.post(`${userBaseEndpoint}/ResetPassword`, { userId, resetToken, newPassword })

export const getUserProfile = () => api.get(`${userBaseEndpoint}/UserProfile`)

export const getPositionsForUser = () => api.get(`${userBaseEndpoint}/PositionsForUser`)

export const updateUserProfile = params => api.put(`${userBaseEndpoint}/Edit`, params)

export const updateUserAuthorProfile = data => api.put(`${userBaseEndpoint}/AuthorProfile`, data)

export const getPositionsForNewUser = roleName => api.get(`${userBaseEndpoint}/PositionsForNewUser?roleName=${roleName}`)

export const getUserByInvitation = inviteToken => api.get(`${userBaseEndpoint}/GetInvitedUserInfo?token=${inviteToken}`)

export const requestInvitationResend = ({ userId, schoolId, locale }) =>
  api.post(`${userBaseEndpoint}/RequestInvitationResend`, {}, { params: { userId, schoolId, locale } })

export const postUserByInvitation = user => api.post(`${userBaseEndpoint}/AcceptInvitation`, user)

export const postExternalUserByInvitation = user => api.post(`${userBaseEndpoint}/AcceptExternalInvitation`, user)

export const inviteUser = params => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/InviteUser`, params, { params: { locale } })
}

export const resendInvitation = (schoolId, ids) => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/ResendInvitation`, ids, { params: { schoolId, locale } })
}

export const sendQuotation = params => api.post(`${userBaseEndpoint}/RequestTrialInformation`, params)

export const trialUser = params => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/RegisterTrialUser`, params, { params: { locale } })
}

export const externalTrialUser = data => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/RegisterExternalTrialUser`, data, { params: { locale } })
}

export const usedMail = params => api.get(`${userBaseEndpoint}/UserExists`, { params })

export const usedMailInSchool = params => api.get(`${userBaseEndpoint}/UserExistsInSchool`, { params })

export const usedMailInSchoolOrSchoolGroupAdmin = params => api.get(`${userBaseEndpoint}/UserExistsInSchoolOrSchoolGroupAdmin`, { params })

export const confirmTrialEmail = (userId, token) => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/ConfirmTrialEmail`, { userId, token }, { params: { locale } })
}

export const acceptChangeSchoolInvitation = user => api.post(`${userBaseEndpoint}/ChangeSchool`, user) // Similar AcceptInvitation

export const getInvitedUsers = () => api.get(`${userBaseEndpoint}/GetInvitedTrialUsers`)

export const sendTrialInvitationsService = params => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/SendTrialUsersInvitation`, params, { params: { locale } })
}

export const requestFreemiumLicense = () => {
  const locale = LocalStorageService.getLang() || defaultLang
  return api.post(`${userBaseEndpoint}/RequestFreemiumLicense`, {}, { params: { locale } })
}

export const getTermsAndConditions = () => api.get(`/krea/Account/TermsAndConditions`)

export const signTermsAndConditions = () => api.post(`/krea/Account/TermsAndConditions/sign`)

export const setLanguageConfiguration = ({ mailingLanguage, navigationLanguage }) => {
  return api.post(`${userBaseEndpoint}/LanguageConfiguration`, { ignoreLocale: true }, {
    params: { DefaultMailingLanguage: mailingLanguage, DefaultNavigationLanguage: navigationLanguage }
  })
}

export const setNavigationTutorialStatus = (status) => api.post(`${userBaseEndpoint}/NavigationTutorial`, status)

export const getSubdomainInfo = (subdomain) => api.get(`${userBaseEndpoint}/GetSubdomain`, { params: { subdomain } })

export const externalGoogleLogin = async (googleToken) => {
  const response = await api.post(`${userBaseEndpoint}/ExternalGoogleLogin`, {}, { params: { token: googleToken } })
  const success = !isError(response.status)
  if (success) {
    const token = response.data.token
    LocalStorageService.setAuthToken(token)
  }
  return response
}

export const externalMicrosoftLogin = async (microsoftToken) => {
  const response = await api.post(`${userBaseEndpoint}/ExternalMicrosoftLogin`, {}, { params: { token: microsoftToken } })
  const success = !isError(response.status)
  if (success) {
    const token = response.data.token
    LocalStorageService.setAuthToken(token)
  }
  return response
}

export const getExternalGoogleInfo = (token) =>
  api.get(`${userBaseEndpoint}/ExternalGoogleInfo`, { params: { token } })

export const getExternalMicrosoftInfo = (token) =>
  api.get(`${userBaseEndpoint}/ExternalMicrosoftInfo`, { params: { token } })

export const getAvailableFilters = (filters) => {
  const params = generateFilters(null, null, filters)
  return api.get(`${userBaseEndpoint}/AvailableFilters?${params.toString()}`)
}

export const getPopularSuggestions = ({ locale, pageNumber = 0, pageSize = 10 }) =>
  api.get(`${userBaseEndpoint}/AvailableSuggestions`, { params: { locale, pageNumber, pageSize } })

export const searchAvailableSuggestions = ({ search, locale, pageNumber = 0, pageSize = 10 }) =>
  api.get(`${userBaseEndpoint}/AvailableSearchSuggestions`, { params: { search, locale, pageNumber, pageSize } })

export const getUserCustomFields = () => api.get(`${userBaseEndpoint}/CustomizedFields`)
